import { ref } from '@vue/reactivity';
import { Toast } from 'vant';
import { CdnUpload, PosterUpload } from '@/utils/upload';
import { fetchPosterUploadToken } from '@/apis/marketing';

// 上传状态
export enum UploadStatusEnum {
  WAIT, // 未开始
  UPLOADING, // 上传中
  SUCCESS, // 成功
  FAIL, // 失败
}

// 上传配置
export interface IUploadConfig {
  // 允许上传的类型，例: image/*
  accept?:string;
  // 最大大小限制，单位K
  maxSize?:number;
  // 最大上传数量
  maxCount?:number;
  // 自定义文件名，如果不传会自动生成一个guid标识
  fileName?:string;
  // 上传路径，不传默认存放在根目录
  path?:string;
  // 是否启用海报上传，因为海报上传有后端逻辑，所以需要特殊处理
  isPoster?:boolean;
}

// 文件信息
export interface IFileInfo {
  // 文件原名
  fileOriginName:string;
  // 文件自定义后的名字
  fileName:string;
  // 文件大小，单位K
  fileSize:number;
  // 文件上传后的URL
  fileUrl?:string;
  // 文件上传进度
  progress:number;
  // bucket名，只针对海报上传
  bucket?:string;
}

/**
 * @description 生成GUID
 * @author Wynne
 * @date 2021-03-24
 * @return {*}
 */
function generateGUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// 上传input实例
let fileInputEle:HTMLInputElement | undefined;

/**
 * @description 生成GUID
 * @author Wynne
 * @date 2021-03-24
 * @param {function} fileUrl 成功回调
 * @param {IUploadConfig} config 上传配置
 */
export const useUpload = (config?:IUploadConfig) => {
  if (config?.path && !/[0-9|a-zA-Z|\-|_]*\/$/.test(config.path)) {
    throw new Error('path只支持中英文/数字/中划线/下划线');
  }
  // 上传进度，百分制
  const files = ref<IFileInfo[]>([]);
  // 上传状态
  const status = ref<UploadStatusEnum>(UploadStatusEnum.WAIT);

  // 开始上传
  const start = (uploadFiles?:File[]):Promise<IFileInfo[]> => new Promise((resolve, reject) => {
    // 进度条信息变更
    const handleProgressChange = (fileName:string, pg:any) => {
      const item = files.value.find((e) => e.fileName === fileName);
      if (item) {
        item.progress = pg.total.percent;
      }
    };

    // 验证是否上传完成
    const checkUploadComplete = () => {
      if ((fileInputEle?.files && fileInputEle.files.length === files.value.filter((e) => e.fileUrl).length) ||
      (uploadFiles && uploadFiles.length === files.value.filter((e) => e.fileUrl).length)) {
        if(fileInputEle)
          fileInputEle.value = '';
        status.value = UploadStatusEnum.SUCCESS;
        resolve(files.value);
      }
    };

    // 文件上传
    const fileUpload = (file:File) => {
      // 是否启用海报上传
      if (config?.isPoster) {
        fetchPosterUploadToken().then((response) => {
          const fileName = response.data.filename;
          const newFile = new File([file], fileName, {
            type: file.type,
          });
          files.value.push({
            fileName: fileName,
            fileOriginName: file.name,
            fileSize: Math.floor(file.size / 1024),
            progress: 0,
          });
          const data = new FormData();
          data.append('token', response.data.token);
          data.append('file', newFile);
          data.append('key', response.data.filename);
          PosterUpload(data, response.data.bucket_url, (progress) => {
            handleProgressChange(fileName, progress);
          }).then((res) => {
            const item = files.value.find((e) => e.fileName === fileName);
            if (item) {
              item.fileUrl = res.fileurl;
              item.bucket = response.data.bucket_url;
            }
            // 校验是否上传完成
            checkUploadComplete();
          }).catch((error) => {
            status.value = UploadStatusEnum.FAIL;
            if (error.code === 'ECONNABORTED') {
              Toast('网络环境较差，上传失败，请稍后重试一下吧~');
            } else {
              Toast('上传失败，请重试一下吧~');
            }
            console.log('上传失败：', error);
            reject(error);
          });
        }).catch((err) => {
          console.log(err);
          Toast({
            type: 'fail',
            message: '获取七牛Token失败',
          });
          reject(err);
        });
      } else {
        // 文件重命名
        const fileExt = file.name.split('.').pop();
        const fileName = `${config?.path || ''}${generateGUID()}.${fileExt}`;
        const newFile = new File([file], fileName, {
          type: file.type,
        });
        // 添加到上传文件记录汇总
        files.value.push({
          fileName: fileName,
          fileOriginName: file.name,
          fileSize: Math.floor(file.size / 1024),
          progress: 0,
        });
        // 常规上传
        CdnUpload(newFile, (progress) => {
          handleProgressChange(fileName, progress);
        }).then((url) => {
          const item = files.value.find((e) => e.fileName === fileName);
          if (item) {
            item.fileUrl = url;
          }
          // 校验是否上传完成
          checkUploadComplete();
        }).catch((error) => {
          status.value = UploadStatusEnum.FAIL;
          if (error.code === 'ECONNABORTED') {
            Toast('网络环境较差，上传失败，请稍后重试一下吧~');
          } else {
            Toast('上传失败，请重试一下吧~');
          }
          console.log('上传失败：', error);
          reject(error);
        });
      };
    };

    // input变更事件
    const handleInputChange = () => {
      const uploadFileList = uploadFiles || fileInputEle?.files;
      // 判断文件为空
      if (!uploadFileList || uploadFileList.length === 0) {
        Toast('请先选择上传文件哦');
        return;
      }
      // 判断超出最大上传数
      if (config?.maxCount && uploadFileList.length > config?.maxCount) {
        Toast(`超出最大上传文件数哦，最多上传${config.maxCount}个文件`);
        return;
      }
      // 判断超出最大文件大小
      if (config?.maxSize) {
        for (let index = 0; index < uploadFileList.length; index++) {
          const file = uploadFileList[index];
          if (file.size / 1024 > config.maxSize) {
            Toast(`超出上传过大哦，最大上传${config.maxSize}K的文件`);
            return;
          }
        }
      }
      // 判断是否符合文件类型
      if (config?.accept) {
        let acceptArr = config.accept.split(',');
        acceptArr = acceptArr.map((e) => {
          e = e.replaceAll('/', '\/');
          e = e.replaceAll('*', '.*');
          return `(${e})`;
        });
        const reg = new RegExp(acceptArr.join('|'));
        for (let index = 0; index < uploadFileList.length; index++) {
          const file = uploadFileList[index];
          if (!reg.test(file.type)) {
            Toast(`文件格式错误，仅支持上传${config.accept}文件`);
            return;
          }
        }
      }
      status.value = UploadStatusEnum.UPLOADING;
      for (const file of uploadFileList) {
        fileUpload(file);
      }
    };

    // 如果没有input实例，则创建input file实例
    if (!fileInputEle) {
      fileInputEle = document.createElement('input');
      fileInputEle.type = 'file';
      fileInputEle.multiple = config?.maxCount !== 1;
      if (config?.accept) {
        fileInputEle.accept = config.accept;
      }
      fileInputEle.addEventListener('change', handleInputChange);
    }
    files.value = [];
    // 如果自定义了上传文件，则不触发input上传
    if (uploadFiles && uploadFiles.length > 0) {
      handleInputChange();
    } else {
      fileInputEle?.click();
    }
  });

  return {
    start,
    files,
    status,
  };
};
